import React, { useState } from 'react';
import { Crop } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Input, Textarea, Label } from '../../ui/Input';
import { updateStore } from '../../../hooks/useStoreApi';
import StoreImageCropModal from '../../StoreImageCropModal';

/**
 * Edit-store dialog used from the admin dashboard. Lets the owner update
 * name, description, profile image and cover image (with crop step).
 */
export function StoreEditModal({ store, onClose, onSave }) {
    const [formData, setFormData] = useState({
        name: store?.name || '',
        about: store?.about || '',
        profileImage: null,
        coverImage: null,
    });
    const [profileImagePreview, setProfileImagePreview] = useState(store?.profile_image || null);
    const [coverImagePreview, setCoverImagePreview] = useState(store?.cover_image || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [tempImageSrc, setTempImageSrc] = useState(null);
    const [cropImageType, setCropImageType] = useState(null); // 'profile' | 'cover'

    const handleImageChange = (e, imageType) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            setError('גודל התמונה גדול מדי. מקסימום 10MB');
            return;
        }
        if (!file.type.startsWith('image/')) {
            setError('יש להעלות קובץ תמונה בלבד');
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            setTempImageSrc(reader.result);
            setCropImageType(imageType);
            setShowCropModal(true);
            setError(null);
        };
        reader.readAsDataURL(file);
    };

    const handleCropComplete = (croppedImage) => {
        if (cropImageType === 'profile') {
            setFormData((f) => ({ ...f, profileImage: croppedImage }));
            setProfileImagePreview(croppedImage);
        } else if (cropImageType === 'cover') {
            setFormData((f) => ({ ...f, coverImage: croppedImage }));
            setCoverImagePreview(croppedImage);
        }
        setShowCropModal(false);
        setTempImageSrc(null);
        setCropImageType(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const updateData = { name: formData.name, about: formData.about };
            if (formData.profileImage) updateData.profile_image_base64 = formData.profileImage;
            if (formData.coverImage)   updateData.cover_image_base64   = formData.coverImage;
            await updateStore(store.store_id, updateData);
            onSave();
        } catch (err) {
            setError(err.message || 'שגיאה בעדכון החנות');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            title="ערוך הגדרות חנות"
            subtitle="עדכן את שם החנות, תיאור והתמונות"
            size="lg"
            footer={
                <>
                    <Button variant="ghost" type="button" onClick={onClose} disabled={loading}>ביטול</Button>
                    <Button variant="primary" type="submit" form="store-edit-form" loading={loading}>
                        שמור שינויים
                    </Button>
                </>
            }
        >
            {error && (
                <div className="mb-4 p-4 bg-danger-50 border border-danger-100 rounded-lg text-danger-700 text-sm">
                    {error}
                </div>
            )}
            <form id="store-edit-form" onSubmit={handleSubmit} className="space-y-5">
                <div>
                    <Label htmlFor="store-edit-name" required>שם החנות</Label>
                    <Input
                        id="store-edit-name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                    />
                </div>

                <div>
                    <Label htmlFor="store-edit-about">תיאור החנות</Label>
                    <Textarea
                        id="store-edit-about"
                        value={formData.about}
                        onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                        rows={4}
                        placeholder="ספר על החנות שלך..."
                    />
                </div>

                <div>
                    <Label>תמונת פרופיל <span className="text-xs text-ink-subtle">(אופציונלי)</span></Label>
                    <div className="flex items-center gap-4">
                        {profileImagePreview && (
                            <div className="w-24 h-24 rounded-pill overflow-hidden border-2 border-line">
                                <img src={profileImagePreview} alt="" className="w-full h-full object-cover" />
                            </div>
                        )}
                        <label className="flex-1 cursor-pointer">
                            <div className="px-4 py-2 border-2 border-dashed border-line rounded-lg hover:border-brand-400 transition-colors text-center flex items-center justify-center gap-2 text-ink-muted">
                                <Crop className="w-4 h-4" />
                                <span className="text-sm">{profileImagePreview ? 'שנה תמונת פרופיל' : 'העלה תמונת פרופיל'}</span>
                            </div>
                            <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, 'profile')} className="hidden" />
                        </label>
                    </div>
                    <p className="text-xs text-ink-subtle mt-1">התמונה תיחתך לריבוע ותידחס באופן אוטומטי</p>
                </div>

                <div>
                    <Label>תמונת כיסוי <span className="text-xs text-ink-subtle">(אופציונלי)</span></Label>
                    <div className="space-y-3">
                        {coverImagePreview && (
                            <div className="w-full aspect-video rounded-lg overflow-hidden border-2 border-line">
                                <img src={coverImagePreview} alt="" className="w-full h-full object-cover" />
                            </div>
                        )}
                        <label className="block cursor-pointer">
                            <div className="px-4 py-3 border-2 border-dashed border-line rounded-lg hover:border-brand-400 transition-colors text-center flex items-center justify-center gap-2 text-ink-muted">
                                <Crop className="w-4 h-4" />
                                <span className="text-sm">{coverImagePreview ? 'שנה תמונת כיסוי' : 'העלה תמונת כיסוי'}</span>
                            </div>
                            <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, 'cover')} className="hidden" />
                        </label>
                    </div>
                    <p className="text-xs text-ink-subtle mt-1">מומלץ תמונה ביחס 16:9, תידחס באופן אוטומטי</p>
                </div>
            </form>

            {showCropModal && tempImageSrc && (
                <StoreImageCropModal
                    imageSrc={tempImageSrc}
                    onClose={() => { setShowCropModal(false); setTempImageSrc(null); setCropImageType(null); }}
                    onCropComplete={handleCropComplete}
                    aspect={cropImageType === 'cover' ? 16 / 9 : 1}
                    mode={cropImageType === 'cover' ? 'cover' : 'profile'}
                />
            )}
        </Modal>
    );
}

export default StoreEditModal;
